import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPalette,
  faTicketAlt,
  faArrowRight,
  faCrown,
  faExclamationTriangle,
  faCheckCircle,
  faPlus,
  faClock,
  faUsers,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

import artTourImage from '../../../assets/tours/art-lovers.webp';
import castleImage from '../../../assets/castle2.png';

const BookingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const ImportantNotice = styled.div`
  background: linear-gradient(135deg, #ff6b35, #f7931e);
  color: white;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 3rem;
  text-align: center;
  box-shadow: 0 4px 20px rgba(255, 107, 53, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.1)"/></svg>') repeat;
    background-size: 20px 20px;
  }
  
  h2 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    position: relative;
    z-index: 1;
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.6;
    position: relative;
    z-index: 1;
    
    &:last-child {
      margin-top: 1rem;
      font-weight: 600;
      font-size: 1.3rem;
    }
  }
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 2rem;
  margin: 3rem 0;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StepCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  text-align: center;
  border: 3px solid ${props => props.primary ? props.theme.colors.accent : '#e0e0e0'};
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
  
  p {
    color: ${props => props.theme.colors.secondary};
    line-height: 1.6;
  }
`;

const PlusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: ${props => props.theme.colors.accent};
  color: white;
  border-radius: 50%;
  font-size: 2rem;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    transform: rotate(90deg);
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
    margin: 1rem auto 0;
  }
`;

const TourDetails = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 3rem;
  
  h3 {
    font-size: 1.8rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: ${props => props.theme.colors.secondary};
    margin-bottom: 1.5rem;
  }
`;

const TourMeta = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const OptionsGrid = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
`;

const OptionCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  max-width: 500px;
  width: 100%;
  
  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const OptionImage = styled.div`
  height: 200px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
`;

const OptionContent = styled.div`
  padding: 2rem;
  text-align: center;
`;

const OptionTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.6rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const OptionDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1.5rem;
`;

const BookButton = styled.a`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-weight: 600;
  border: 2px solid ${props => props.theme.colors.accent};
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
  
  svg {
    margin-left: 0.5rem;
  }
`;

const UpsellNotice = styled.div`
  background: #f8f9fa;
  border: 2px dashed ${props => props.theme.colors.accent};
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
  text-align: center;
  
  p {
    margin: 0;
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
    
    svg {
      color: ${props => props.theme.colors.accent};
      margin-right: 0.5rem;
    }
  }
`;

const ArtCollectionTour = () => {
  return (
    <BookingContainer>
      <ParallaxBanner 
        title="Art Collection Day Tour"
        subtitle="Discover the Duke of Rutland's Extensive Art Collection"
        image={artTourImage}
      />
      
      <ContentContainer>
        <ProcessSteps>
          <StepCard primary>
            <h3>
              <FontAwesomeIcon icon={faTicketAlt} />
              STEP 1: Get Castle Entry Ticket
            </h3>
            <p>
              Start by purchasing your Castle & Gardens entry ticket. 
              The Art Collection Tour add-on will be available during checkout.
            </p>
          </StepCard>
          
          <PlusIcon>
            <FontAwesomeIcon icon={faPlus} />
          </PlusIcon>
          
          <StepCard>
            <h3>
              <FontAwesomeIcon icon={faPalette} />
              STEP 2: Add Art Tour
            </h3>
            <p>
              During the booking process, you'll see the option to add the Art Collection Tour 
              for just £5 per person to your order.
            </p>
          </StepCard>
        </ProcessSteps>

        <SectionTitle>Step 1: Get Your Castle Entry Ticket</SectionTitle>
        
        <OptionsGrid>
          <OptionCard>
            <OptionImage image={castleImage} />
            <OptionContent>
              <OptionTitle>
                <FontAwesomeIcon icon={faCrown} />
                Castle & Gardens Entry
              </OptionTitle>
              <OptionDescription>
                Full access to the Castle State Rooms and Gardens. This ticket is required 
                for the Art Collection Tour which takes place in the castle's historic State Rooms.
              </OptionDescription>
              <BookButton href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=7b374807-b3b6-4bda-9f1f-2dd6f772b4fa" target="_blank" rel="noopener noreferrer">
                Book Castle Entry & Add Art Tour <FontAwesomeIcon icon={faArrowRight} />
              </BookButton>
              <UpsellNotice>
                <p>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Art Collection Tour (£5pp) available as add-on during booking
                </p>
              </UpsellNotice>
            </OptionContent>
          </OptionCard>
        </OptionsGrid>

        <TourDetails style={{ marginTop: '4rem' }}>
          <h3>
            <FontAwesomeIcon icon={faCalendarAlt} />
            Available Tour Dates
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <strong>1st August</strong><br />
              11:00 AM
            </div>
            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <strong>29th August</strong><br />
              11:00 AM
            </div>
          </div>
        </TourDetails>

        <TourDetails>
          <h3>
            <FontAwesomeIcon icon={faPalette} />
            About the Art Collection Day Tour
          </h3>
          <p>
            Every picture tells a story – enjoy a 1 ½ hour tour with our expert guide of the Duke of Rutland's 
            extensive art collection. Discover masterpieces from renowned artists, learn about the history 
            and provenance of the works, and gain insights into centuries of collecting by the Dukes of Rutland. 
            This daytime tour offers a perfect opportunity to appreciate the castle's artistic treasures in the 
            beautiful natural light of day.
          </p>
          <TourMeta>
            <div>
              <FontAwesomeIcon icon={faClock} /> Duration: 1½ hours
            </div>
            <div>
              <FontAwesomeIcon icon={faUsers} /> Small groups for better experience
            </div>
          </TourMeta>
        </TourDetails>
      </ContentContainer>
      
      <Footer />
    </BookingContainer>
  );
};

export default ArtCollectionTour; 